/**
 * The Design | Sketch | Permit switch (components/WorkspaceSwitch.tsx): the three views of one design, in the order we
 * sell them. Pure (routes, what each view needs, what a switch does with unsaved work), so it is unit tested.
 *
 * - Design: the Designer's editor, /designs/<id>/edit (a Sketch's current version opens there like any design).
 * - Sketch: the Sketch tools, /designs/<id>/sketch; on an automatic design they refine the detected roof (faces the
 *   installer does not touch stay as detected; docs/features/sketch.md § 1).
 * - Permit: the Permit workspace, /designs/<id>/permit.
 *
 * Keys (editor/hotkeys.ts, rebindable): Shift+D Design, Shift+E Sketch, Shift+P Permit; in the Permit workspace
 * Shift+P goes back to Design as before.
 *
 * Unsaved work never travels silently: from an editor with unsaved changes the switch asks first (Save and switch,
 * or stay; to the Permit workspace also "Open without saving", which leaves the edits in the editor as before). The
 * Permit workspace autosaves, so it saves (flushes) and switches. "Unsaved" means differs from the saved version
 * (editor/model.ts `withDirty`, sketch/state.ts `withSketchDirty`); a save that changes nothing goes on (`saveThenGo`),
 * a failed one stays with the reason and Retry.
 */

import type { MessageKey } from "../i18n/keys";

export type Workspace = "design" | "sketch" | "permit";

export const WORKSPACES: Workspace[] = ["design", "sketch", "permit"];

/** Each view's name, as a catalog key (resolve with t()). */
export const WORKSPACE_LABEL_KEYS: Record<Workspace, MessageKey> = { design: "editor.workspace.design", sketch: "editor.workspace.sketch", permit: "editor.workspace.permit" };

/** @deprecated English names, kept for components/WorkspaceSwitch.tsx until it reads WORKSPACE_LABEL_KEYS. */
export const WORKSPACE_LABELS: Record<Workspace, string> = { design: "Design", sketch: "Sketch", permit: "Permit" };

/** The hotkeys.ts action of each view. */
export const WORKSPACE_ACTIONS: Record<Workspace, string> = { design: "mode.design", sketch: "mode.sketch", permit: "mode.permit" };

const PATHS: Record<Workspace, string> = { design: "edit", sketch: "sketch", permit: "permit" };

export function workspaceHref(designId: string, workspace: Workspace): string {
  return `/designs/${designId}/${PATHS[workspace]}`;
}

/** The view a pathname shows, if it is one of the three. */
export function workspaceOf(pathname: string): Workspace | null {
  const m = /^\/designs\/[^/]+\/(edit|sketch|permit)\/?$/.exec(pathname);
  if (!m) return null;
  return (Object.keys(PATHS) as Workspace[]).find((w) => PATHS[w] === m[1]) ?? null;
}

/** The action id a hotkey fired → the view it opens, if it is a view switch. In the Permit workspace Shift+P toggles
 * back to Design (as it always did). */
export function workspaceForAction(actionId: string, current: Workspace): Workspace | null {
  const target = (Object.keys(WORKSPACE_ACTIONS) as Workspace[]).find((w) => WORKSPACE_ACTIONS[w] === actionId) ?? null;
  if (target === "permit" && current === "permit") return "design";
  return target;
}

export interface DesignShape {
  /** `sketch` or `automatic`. */
  mode?: string | null;
  /** The saved roof model (null on a Sketch nobody has drawn and saved yet). */
  hasRoof: boolean;
  /** Saved panels. */
  hasPanels?: boolean;
}

/** Whether a view can open for this design, and why not. Sketch always can (it is where a roof is drawn); the
 * Designer and the Permit workspace need a saved roof (a new Sketch has none until its first save). */
export function workspaceAvailabilityKeys(design: DesignShape): Record<Workspace, MessageKey | null> {
  const noRoof: MessageKey | null = design.hasRoof ? null : "editor.workspace.needsRoof";
  return { design: noRoof, sketch: null, permit: noRoof };
}

/** @deprecated The same in English, kept for components/WorkspaceSwitch.tsx until it reads workspaceAvailabilityKeys. */
export function workspaceAvailability(design: DesignShape): Record<Workspace, string | null> {
  const noRoof = design.hasRoof ? null : "Draw the roof and save first";
  return { design: noRoof, sketch: null, permit: noRoof };
}

export interface SwitchState {
  /** Unsaved changes in the current view. */
  dirty: boolean;
  /** The view cannot save (an old version, a view-only role): nothing to keep. */
  readOnly: boolean;
  /** The view saves by itself (the Permit workspace's autosave): flush, then go. */
  autosaves?: boolean;
}

export type SwitchPlan =
  | { kind: "stay" }
  | { kind: "go"; href: string }
  | { kind: "flush"; href: string }
  | { kind: "ask"; href: string; target: Workspace; allowWithoutSaving: boolean };

/** What a click or a key on the switch does. */
export function planSwitch(designId: string, from: Workspace, to: Workspace, state: SwitchState, design?: DesignShape): SwitchPlan {
  if (from === to) return { kind: "stay" };
  if (design && workspaceAvailability(design)[to]) return { kind: "stay" };
  const href = workspaceHref(designId, to);
  if (state.autosaves) return { kind: "flush", href };
  if (!state.dirty || state.readOnly) return { kind: "go", href };
  // Permit reads the saved version and the edits stay in this tab, as before; the other editor would open the saved
  // version and lose them, so there it is save or stay.
  return { kind: "ask", href, target: to, allowWithoutSaving: to === "permit" };
}

/** The question the switch asks over unsaved work, as catalog keys. The switch asks only from an editor that does
 * not autosave (the Designer or the Sketch), so going to Sketch means leaving the Designer and back. */
export function askKeys(to: Workspace): { title: MessageKey; body: MessageKey } {
  if (to === "permit") return { title: "editor.workspace.ask.permit.title", body: "editor.workspace.ask.permit.body" };
  if (to === "sketch") return { title: "editor.workspace.ask.toSketch.title", body: "editor.workspace.ask.toSketch.body" };
  return { title: "editor.workspace.ask.toDesign.title", body: "editor.workspace.ask.toDesign.body" };
}

/** @deprecated The question in English, kept for components/WorkspaceSwitch.tsx until it reads askKeys. */
export function askText(from: Workspace, to: Workspace): { title: string; body: string } {
  const where = to === "permit" ? "the Permit workspace" : to === "sketch" ? "the Sketch tools" : "the Designer";
  if (to === "permit")
    return {
      title: "Save before opening the Permit workspace?",
      body: "The plan set is made from a saved version. Without saving, the workspace uses the current saved version and your unsaved changes stay here only until you leave the editor.",
    };
  return {
    title: `Save before switching to ${to === "sketch" ? "Sketch" : "Design"}?`,
    body: `${where[0]!.toUpperCase()}${where.slice(1)} open${to === "sketch" ? "" : "s"} the saved design. Save your changes in ${from === "sketch" ? "the Sketch" : "the Designer"} as a new version first, so nothing is lost.`,
  };
}

export type SaveOutcome = { ok: true } | { ok: false; error: unknown };

/** "Save and switch" (or the Permit workspace's flush, then switch): `go` runs once the save resolved, a no-op save
 * included (the API answers an edit that changes nothing with 200 `unchanged`, the autosave resolves at once when
 * nothing is pending). A failed save never goes: the outcome carries the error for the dialog's message and Retry. */
export async function saveThenGo(save: () => Promise<unknown>, go: () => void): Promise<SaveOutcome> {
  try {
    await save();
  } catch (error) {
    return { ok: false, error };
  }
  go();
  return { ok: true };
}

/** A message as a sentence: ends with a full stop unless it already ends with one (or ? or !). */
export function asSentence(text: string): string {
  const t = text.trim();
  return !t || /[.!?…]["')\]]?$/.test(t) ? t : `${t}.`;
}
